import React, { FC, ReactElement } from 'react';
import { PopoverOrigin } from '@material-ui/core/Popover';
import styled from 'styled-components';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { useOcPopover } from '../Popover';
import OcPopover from '../Popover/OcPopover';
import Typography from '../Typography/Typography';
import { ActiveDiv } from '../ActiveDiv';

export interface PreviewCardFieldProp {
  label?: string;
  content?: ReactElement | JSX.Element | string;
  key?: string;
}
export interface PreviewCardProps {
  cardTitle: string;
  cardFields: PreviewCardFieldProp[];
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  dataTest?: string;
  footer?: ReactElement | JSX.Element | string;
  children?: React.ReactNode;
}

export const PreviewCard: FC<React.PropsWithChildren<PreviewCardProps>> = ({
  dataTest,
  children,
  cardFields,
  cardTitle,
  anchorOrigin,
  transformOrigin,
  footer
}) => {
  const popover = useOcPopover();

  const childrenIsString = typeof children === 'string';

  const label = childrenIsString ? children : (dataTest ?? 'custom');

  return (
    <>
      {childrenIsString ? (
        <StyledButton data-test={dataTest ?? `preview-card-btn-${children}`} onClick={popover.toggle}>
          {children}
        </StyledButton>
      ) : (
        <StyledActiveDiv $hideFocus data-test={dataTest ?? 'preview-card-btn'} onClick={popover.toggle}>
          {children}
        </StyledActiveDiv>
      )}
      <OcPopover
        dataTest={`preview-card-popover-${label}`}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.close}
      >
        <StyledPopoverContent>
          <Typography dataTest={`preview-card-title-${label}`} color={tokens.colorOnSurface} variant="label1">
            {cardTitle}
          </Typography>
          <StyledFields>
            {cardFields.map((field) => (
              <React.Fragment key={field.key ?? field.label}>
                <Typography
                  dataTest={`preview-card-label-${field.label}`}
                  variant="label1"
                  color={tokens.colorOnSurfaceVariant}
                >
                  {field.label}
                </Typography>
                {typeof field.content === 'string' ? (
                  <Typography
                    dataTest={`preview-card-content-${field.label}`}
                    variant="body1"
                    color={tokens.colorOnSurface}
                  >
                    {field.content}
                  </Typography>
                ) : (
                  <div>{field.content}</div>
                )}
              </React.Fragment>
            ))}
          </StyledFields>
        </StyledPopoverContent>
        {footer && (
          <>
            <StyledDivider />
            <StyledFooter>{footer}</StyledFooter>
          </>
        )}
      </OcPopover>
    </>
  );
};

const StyledFooter = styled.div`
  padding: ${({ theme }) => theme.spacingSize.l};
`;

const StyledActiveDiv = styled(ActiveDiv)`
  text-decoration: underline dotted;
  display: inline-block;
`;

const StyledDivider = styled.div`
  box-sizing: border-box;
  width: 440px;
  height: 1px;
  border-bottom: 1px solid ${({ theme }) => theme.colorSurfaceVariant};
  flex: none;
  align-self: stretch;
  flex-grow: 0;
`;

const StyledFields = styled.div`
  padding-top: ${({ theme }) => theme.spacingSize.l};
  display: grid;
  grid-template-columns: auto auto;
  column-gap: 80px;
  row-gap: ${({ theme }) => theme.spacingSize.s};
`;

const StyledPopoverContent = styled.div`
  padding: ${({ theme }) => theme.spacingSize.l};
`;

const StyledButton = styled.a`
  text-decoration: underline dotted;
  padding: 0 ${({ theme }) => theme.spacingSize.s};
  cursor: pointer;
  background-color: transparent;
`;
